import { useMemo } from 'react';
import { isEqual } from 'lodash';

import { invariant } from '@e-socna/utils';
import { EMPTY_OBJECT } from '@e-socna/empty';
import type { LatLonCoordinates } from '@e-socna/gis';

import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useGeoCodingSearchQuery } from '../../../components/Map/hooks/geopl/useGeoCodingSearchQuery.hook';
import { useMemoCompare } from '../../../hooks/useMemoCompare.hook';
import type { AddressCardCmp_Address } from '../../../components/Addresses/Card';
import type { AddressWithMapCmp_Address } from '../../../components/AddressWithMap';

import type { AddressForm_Address } from '../fragments/address.fragment';

interface Params {
  readonly address:
    | AddressForm_Address
    | AddressCardCmp_Address
    | AddressWithMapCmp_Address
    | undefined
    | null;
  readonly enabled?: boolean;
}

type Result =
  | Readonly<{
      isLoading: true;
      data: undefined;
    }>
  | Readonly<{
      isLoading: false;
      data: LatLonCoordinates | undefined;
    }>;

export const useFromAddressToLatLon = ({
  address,
  enabled = !!address,
}: Readonly<Params>): Result => {
  const { projectionsCodeMap } = useReferencesContext();

  const locationInfo = address?.locationInfo;
  const postalInfo = address?.postalInfo;
  const cadastralInfo = address?.cadastralInfo;

  invariant(
    !locationInfo ||
      locationInfo.projectionId === projectionsCodeMap.Epsg4326._id,
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    `Unsupported projection : ${locationInfo?.projectionId || 'undefined'}`,
  );

  const query = useGeoCodingSearchQuery({
    ...(!locationInfo && postalInfo
      ? {
          index: 'address',
          q: `${postalInfo.streetNumber} ${postalInfo.route} ${postalInfo.postalCode} ${postalInfo.locality}`,
        }
      : EMPTY_OBJECT),
    ...(!locationInfo && !postalInfo && cadastralInfo
      ? {
          index: 'parcel',
          departmentcode: cadastralInfo.departmentCode,
          municipalitycode: cadastralInfo.municipalityCode,
          oldmunicipalitycode: cadastralInfo.oldMunicipalityCode,
          districtcode: cadastralInfo.districtCode,
          section: cadastralInfo.section,
          number: cadastralInfo.number,
          sheet: cadastralInfo.sheet,
        }
      : EMPTY_OBJECT),
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    enabled: !locationInfo && enabled && !!(postalInfo || cadastralInfo),
  });

  const result = useMemo(() => {
    const isLoading = locationInfo ? false : query.isLoading;

    const data = locationInfo
      ? {
          lon: locationInfo.coordinates[0],
          lat: locationInfo.coordinates[1],
        }
      : isLoading
        ? undefined
        : {
            lon: query.data?.features[0]?.geometry.coordinates[0],
            lat: query.data?.features[0]?.geometry.coordinates[1],
          };

    const cleanData = (data && (!data.lat || !data.lon) ? undefined : data) as
      LatLonCoordinates | undefined;

    return {
      isLoading,
      data: cleanData,
    } as Result;
  }, [locationInfo, query.data?.features, query.isLoading]);

  return useMemoCompare(result, isEqual);
};
